// src/pages/KarigarManagement/Job/hooks/useJobForm.js

import { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { useJobManagement } from "@/hooks/useKarigar";
import { BASE_API_URL } from "@/services/api";
import {
  getDefaultFormValues,
  needsItemDetails as checkNeedsItemDetails,
  needsCustomer as checkNeedsCustomer,
  canSelectProduct as checkCanSelectProduct,
  isStandaloneJob as checkIsStandaloneJob,
  JOB_TYPE_TO_WORK_TYPE,
} from "../constants/jobConstants";
import {
  extractProductImages,
  extractProductStones,
  extractProductKarigars,
  getProductFormData,
  getJobFormData,
  extractJobImages,
  extractJobStones,
  buildJobPayload,
  validateStep,
  getStepValidationFields,
  getMetalTypeFromPurity,
} from "../utils/jobFormUtils";

/**
 * Custom hook for job form logic
 * Supports both create and edit modes
 */
export const useJobForm = ({
  mode = "create", // "create" | "edit"
  initialJob = null,
  onSuccess = null,
  redirectPath = "/karigar-management/jobs",
}) => {
  const navigate = useNavigate();

  const {
    loading: jobLoading,
    error: jobError,
    successMessage,
    addJob,
    addRepairJob,
    addAlterationJob,
    addInternalJob,
    updateJob,
    clearError,
    clearSuccessMessage,
  } = useJobManagement();

  // ============================================
  // STATE
  // ============================================

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Karigar
  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [suggestedKarigars, setSuggestedKarigars] = useState([]);
  const [showKarigarDialog, setShowKarigarDialog] = useState(false);

  // Stones
  const [stones, setStones] = useState([]);

  // Images
  const [referenceImages, setReferenceImages] = useState([]); // New uploaded files
  const [itemImages, setItemImages] = useState([]); // New uploaded files
  const [productImageUrls, setProductImageUrls] = useState([]); // URLs from product
  const [removedProductImageIndexes, setRemovedProductImageIndexes] = useState(
    new Set()
  );
  const [existingReferenceImages, setExistingReferenceImages] = useState([]); // Existing URLs (edit mode)
  const [existingItemImages, setExistingItemImages] = useState([]); // Existing URLs (edit mode)
  const [removedExistingImageIndexes, setRemovedExistingImageIndexes] =
    useState(new Set());
  const [previewImage, setPreviewImage] = useState(null);

  // User selection
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);

  // Order selection
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderItem, setSelectedOrderItem] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);

  // Product selection
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);

  // ============================================
  // FORM SETUP
  // ============================================

  const defaultValues = useMemo(() => {
    if (mode === "edit" && initialJob) {
      return getDefaultFormValues(getJobFormData(initialJob));
    }
    return getDefaultFormValues({
      expectedCompletionDate: format(addDays(new Date(), 7), "yyyy-MM-dd"),
    });
  }, [mode, initialJob]);

  const form = useForm({
    mode: "onChange",
    defaultValues,
  });

  const {
    watch,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  // Watch values
  const watchJobType = watch("jobType");
  const watchMetalType = watch("specifications.metalType");
  const watchProductType = watch("specifications.productType");
  const watchIsUrgent = watch("isUrgent");
  const watchPriority = watch("priority");
  const watchExpectedDate = watch("expectedCompletionDate");

  // ============================================
  // DERIVED STATE
  // ============================================

  const isStandaloneJob = checkIsStandaloneJob(watchJobType);
  const isInternalJob = watchJobType === "INTERNAL";
  const needsItemDetails = checkNeedsItemDetails(watchJobType);
  const needsCustomer = checkNeedsCustomer(watchJobType);
  const canSelectProduct = checkCanSelectProduct(watchJobType);

  const activeProductImages = useMemo(() => {
    return productImageUrls.filter(
      (_, index) => !removedProductImageIndexes.has(index)
    );
  }, [productImageUrls, removedProductImageIndexes]);

  const activeExistingImages = useMemo(() => {
    return existingReferenceImages.filter(
      (_, index) => !removedExistingImageIndexes.has(index)
    );
  }, [existingReferenceImages, removedExistingImageIndexes]);

  // ============================================
  // EFFECTS
  // ============================================

  // Initialize edit mode data
  useEffect(() => {
    if (mode === "edit" && initialJob) {
      // Load existing images
      const {
        referenceImages: existingRefImages,
        itemImages: existingItmImages,
      } = extractJobImages(initialJob);
      setExistingReferenceImages(existingRefImages);
      setExistingItemImages(existingItmImages);

      // Load existing stones
      const existingStones = extractJobStones(initialJob);
      setStones(existingStones);

      // Load karigar if assigned
      if (initialJob.karigarId) {
        setSelectedKarigar({
          _id: initialJob.karigarId,
          karigarId: initialJob.karigarId,
          name: initialJob.karigarName,
          karigarCode: initialJob.karigarCode,
        });
      }

      // Load user if exists
      if (initialJob.userId || initialJob.customerId) {
        setSelectedUser({
          _id: initialJob.userId || initialJob.customerId,
          name: initialJob.customerName || initialJob.userName,
          phoneNumber: initialJob.customerPhone || initialJob.userPhone,
        });
      }

      // Load order if linked
      if (initialJob.orderId) {
        setSelectedOrder({
          orderId: initialJob.orderId,
          orderNumber: initialJob.orderNumber,
        });
      }

      // Load product if linked
      if (initialJob.productId) {
        setSelectedProduct({
          _id: initialJob.productId,
          name: initialJob.productName,
          sku: initialJob.productSKU,
        });
      }
    }
  }, [mode, initialJob]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(redirectPath);
      }
    }
    if (jobError) {
      toast.error(jobError);
      clearError();
    }
  }, [
    successMessage,
    jobError,
    navigate,
    redirectPath,
    onSuccess,
    clearSuccessMessage,
    clearError,
  ]);

  // Auto-set priority when urgent is toggled
  useEffect(() => {
    if (
      watchIsUrgent &&
      watchPriority !== "URGENT" &&
      watchPriority !== "CRITICAL"
    ) {
      setValue("priority", "URGENT");
    }
  }, [watchIsUrgent, watchPriority, setValue]);

  // Auto-set work type based on job type
  useEffect(() => {
    if (JOB_TYPE_TO_WORK_TYPE[watchJobType]) {
      setValue("workType", JOB_TYPE_TO_WORK_TYPE[watchJobType]);
    }
  }, [watchJobType, setValue]);

  // ============================================
  // NAVIGATION
  // ============================================

  const canProceedToNext = useCallback(() => {
    const values = getValues();
    return validateStep(currentStep, values, {
      needsCustomer,
      needsItemDetails,
    });
  }, [currentStep, getValues, needsCustomer, needsItemDetails]);

  const goToNextStep = useCallback(async () => {
    const fieldsToValidate = getStepValidationFields(currentStep, {
      needsCustomer,
      needsItemDetails,
    });
    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid && currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, trigger, needsCustomer, needsItemDetails]);

  const goToPrevStep = useCallback(() => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= 6) {
      setCurrentStep(step);
    }
  }, []);

  // ============================================
  // PRODUCT SELECTION
  // ============================================

  const handleProductSelected = useCallback(
    (product) => {
      if (!product) return;

      setSelectedProduct(product);
      setRemovedProductImageIndexes(new Set());

      const productFormData = getProductFormData(product, watchJobType);
      if (!productFormData) return;

      // Set form values
      setValue("productId", productFormData.productId);
      setValue("productName", productFormData.productName);
      setValue("productSKU", productFormData.productSKU);
      setValue("categoryId", productFormData.categoryId);
      setValue("categoryName", productFormData.categoryName);

      // Auto-generate title/description if empty
      if (!getValues("title")) {
        setValue("title", productFormData.title);
      }
      if (!getValues("description")) {
        setValue("description", productFormData.description);
      }

      // Set specifications
      Object.entries(productFormData.specifications).forEach(([key, value]) => {
        if (value) setValue(`specifications.${key}`, value);
      });

      // Set timeline
      if (productFormData.estimatedHours) {
        setValue("estimatedHours", productFormData.estimatedHours);
      }
      if (productFormData.expectedCompletionDate) {
        setValue(
          "expectedCompletionDate",
          productFormData.expectedCompletionDate
        );
      }

      // Set special instructions with difficulty
      if (productFormData.difficultyNote) {
        const currentInstructions = getValues("specialInstructions") || "";
        if (!currentInstructions.includes("Difficulty:")) {
          setValue(
            "specialInstructions",
            currentInstructions
              ? `${currentInstructions}\n${productFormData.difficultyNote}`
              : productFormData.difficultyNote
          );
        }
      }

      // Extract and set images
      const imageUrls = extractProductImages(product);
      setProductImageUrls(imageUrls);

      // Extract and set stones
      const productStones = extractProductStones(product);
      if (productStones.length > 0) {
        setStones(productStones);
      }

      // Extract and set suggested karigars
      const karigars = extractProductKarigars(product);
      setSuggestedKarigars(karigars);

      toast.success(
        <div className="flex flex-col">
          <span className="font-medium">Product "{product.name}" selected</span>
          <span className="text-sm text-muted-foreground">
            Form auto-filled. Review and edit as needed.
          </span>
        </div>
      );
    },
    [watchJobType, setValue, getValues]
  );

  const clearSelectedProduct = useCallback(() => {
    setSelectedProduct(null);
    setProductImageUrls([]);
    setRemovedProductImageIndexes(new Set());
    setSuggestedKarigars([]);

    setValue("productId", "");
    setValue("productName", "");
    setValue("productSKU", "");
    setValue("categoryId", "");
    setValue("categoryName", "");

    toast.info("Product selection cleared");
  }, [setValue]);

  // ============================================
  // USER SELECTION
  // ============================================

  const handleUserSelected = useCallback(
    (user) => {
      if (!user) return;
      setSelectedUser(user);
      setValue("customerName", user.name || "");
      setValue("customerPhone", user.phoneNumber || "");
      setValue("customerId", user._id || user.customerId || "");
      toast.success(`Customer ${user.name} selected`);
    },
    [setValue]
  );

  const clearSelectedUser = useCallback(() => {
    setSelectedUser(null);
    setValue("customerName", "");
    setValue("customerPhone", "");
    setValue("customerId", "");
  }, [setValue]);

  // ============================================
  // ORDER SELECTION
  // ============================================

  const handleOrderSelected = useCallback(
    (result) => {
      if (!result) return;
      const {
        orderId,
        orderMongoId,
        order,
        itemIndex,
        item,
        customer,
        itemDetails,
      } = result;

      setSelectedOrder(order);
      setSelectedOrderItem(item);
      setValue("orderId", orderId);
      setValue("orderMongoId", orderMongoId);
      setValue("orderItemIndex", itemIndex);

      if (customer) {
        if (customer.userId) setValue("customerId", customer.userId);
        if (customer.name) setValue("customerName", customer.name);
        if (customer.phone) setValue("customerPhone", customer.phone);
      }

      if (item && itemDetails) {
        if (!getValues("title")) {
          const workType =
            itemDetails.workType || itemDetails.subType || "Work";
          const itemName = itemDetails.name || "Item";
          setValue("title", `${workType} - ${itemName}`);
        }
        if (itemDetails.type)
          setValue("specifications.metalType", itemDetails.type);
        if (itemDetails.subType)
          setValue("specifications.productType", itemDetails.subType);
        if (itemDetails.purity)
          setValue("specifications.purity", itemDetails.purity);
        if (itemDetails.weight)
          setValue("specifications.weight", itemDetails.weight);
        if (itemDetails.workType) setValue("workType", itemDetails.workType);
      }

      toast.success(
        `Order ${orderId} selected${item ? ` (Item #${itemIndex + 1})` : ""}`
      );
    },
    [setValue, getValues]
  );

  const clearSelectedOrder = useCallback(() => {
    setSelectedOrder(null);
    setSelectedOrderItem(null);
    setValue("orderId", "");
    setValue("orderItemIndex", undefined);
  }, [setValue]);

  // ============================================
  // STONE MANAGEMENT
  // ============================================

  const addStone = useCallback(() => {
    setStones((prev) => [
      ...prev,
      { type: "", weight: "", quantity: 1, notes: "" },
    ]);
  }, []);

  const updateStone = useCallback((index, field, value) => {
    setStones((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  }, []);

  const removeStone = useCallback((index) => {
    setStones((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ============================================
  // IMAGE MANAGEMENT
  // ============================================

  const handleRemoveProductImage = useCallback((index) => {
    setRemovedProductImageIndexes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
        toast.info("Image restored");
      } else {
        newSet.add(index);
        toast.info("Image removed from selection");
      }
      return newSet;
    });
  }, []);

  const handleRestoreAllProductImages = useCallback(() => {
    setRemovedProductImageIndexes(new Set());
    toast.success("All product images restored");
  }, []);

  const handleRemoveAllProductImages = useCallback(() => {
    const allIndexes = new Set(productImageUrls.map((_, i) => i));
    setRemovedProductImageIndexes(allIndexes);
    toast.info("All product images removed from selection");
  }, [productImageUrls]);

  const handleRemoveExistingImage = useCallback((index) => {
    setRemovedExistingImageIndexes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
        toast.info("Image restored");
      } else {
        newSet.add(index);
        toast.info("Image removed");
      }
      return newSet;
    });
  }, []);

  const handleRestoreAllExistingImages = useCallback(() => {
    setRemovedExistingImageIndexes(new Set());
    toast.success("All existing images restored");
  }, []);

  // ============================================
  // UTILITY: Convert URL to File/Blob (using proxy)
  // ============================================

  const urlToFile = useCallback(async (url, index) => {
    try {
      console.log(`[JobForm] Fetching image ${index + 1} via proxy: ${url}`);

      // Use the image proxy to bypass CORS
      const proxyUrl = `${BASE_API_URL}/image-proxy?url=${encodeURIComponent(
        url
      )}`;

      console.log(`[JobForm] Proxy URL: ${proxyUrl}`);

      const response = await fetch(proxyUrl);

      if (!response.ok) {
        throw new Error(`Proxy error! status: ${response.status}`);
      }

      const blob = await response.blob();
      console.log(`[JobForm] Got blob for image ${index + 1}:`, {
        size: blob.size,
        type: blob.type,
      });

      if (blob.size === 0) {
        throw new Error("Blob size is 0");
      }

      // Extract filename from URL or generate one
      const urlParts = url.split("/");
      const originalName =
        urlParts[urlParts.length - 1]?.split("?")[0] ||
        `product-image-${index}`;

      // Determine file extension from blob type or URL
      let mimeType = blob.type;
      if (!mimeType || mimeType === "application/octet-stream") {
        const ext = originalName.split(".").pop()?.toLowerCase();
        const mimeMap = {
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          gif: "image/gif",
          webp: "image/webp",
        };
        mimeType = mimeMap[ext] || "image/jpeg";
      }

      const extension = mimeType.split("/")[1] || "jpeg";
      const fileName = originalName.includes(".")
        ? originalName
        : `product-image-${index + 1}.${extension}`;

      // Create File object from Blob
      const file = new File([blob], fileName, {
        type: mimeType,
        lastModified: Date.now(),
      });

      console.log(`[JobForm] ✅ Created file for image ${index + 1}:`, {
        name: file.name,
        size: file.size,
        type: file.type,
        isFile: file instanceof File,
      });

      return file;
    } catch (error) {
      console.error(
        `[JobForm] ❌ Proxy fetch failed for image ${index + 1}: ${url}`,
        error.message
      );
      return null;
    }
  }, []);

  const convertUrlsToFiles = useCallback(
    async (urls) => {
      if (!urls || urls.length === 0) {
        console.log("[JobForm] No URLs to convert");
        return [];
      }

      console.log(
        `[JobForm] Starting conversion of ${urls.length} URLs to files...`
      );

      const filePromises = urls.map((url, index) => urlToFile(url, index));
      const files = await Promise.all(filePromises);

      // Filter out any failed conversions
      const validFiles = files.filter(
        (file) => file !== null && file instanceof File
      );

      console.log(
        `[JobForm] Conversion complete: ${validFiles.length}/${urls.length} successful`
      );

      // Log each valid file
      validFiles.forEach((file, i) => {
        console.log(
          `[JobForm] Valid file ${i + 1}:`,
          file.name,
          file.size,
          "bytes"
        );
      });

      return validFiles;
    },
    [urlToFile]
  );

  // ============================================
  // FORM SUBMIT
  // ============================================

  const onSubmit = useCallback(
    async (data) => {
      setSubmitting(true);

      console.log("=".repeat(60));
      console.log("[JobForm] ========== SUBMIT START ==========");
      console.log("[JobForm] Mode:", mode);
      console.log(
        "[JobForm] Active Product Images (URLs):",
        activeProductImages
      );
      console.log(
        "[JobForm] Active Existing Images (URLs):",
        activeExistingImages
      );
      console.log("[JobForm] Reference Images (Files):", referenceImages);
      console.log("[JobForm] Item Images (Files):", itemImages);
      console.log("=".repeat(60));

      try {
        const payload = buildJobPayload({
          formData: data,
          selectedProduct,
          selectedUser,
          selectedOrder,
          selectedOrderItem,
          selectedKarigar,
          stones,
          activeProductImages,
          needsCustomer,
          needsItemDetails,
        });

        // Step 1: Convert product image URLs to binary files
        let productImageFiles = [];
        if (activeProductImages.length > 0) {
          console.log("[JobForm] Step 1: Converting product images...");
          console.log("[JobForm] URLs to convert:", activeProductImages);

          try {
            productImageFiles = await convertUrlsToFiles(activeProductImages);
            console.log(
              "[JobForm] Step 1 Result:",
              productImageFiles.length,
              "files created"
            );
            productImageFiles.forEach((f, i) => {
              console.log(
                `  File ${i + 1}:`,
                f?.name,
                f?.size,
                f?.type,
                "isFile:",
                f instanceof File
              );
            });
          } catch (err) {
            console.error("[JobForm] Step 1 ERROR:", err);
          }
        } else {
          console.log("[JobForm] Step 1: No product images to convert");
        }

        // Step 2: Convert existing image URLs to binary files (for edit mode)
        let existingImageFiles = [];
        if (activeExistingImages.length > 0) {
          console.log("[JobForm] Step 2: Converting existing images...");
          console.log("[JobForm] URLs to convert:", activeExistingImages);

          try {
            existingImageFiles = await convertUrlsToFiles(activeExistingImages);
            console.log(
              "[JobForm] Step 2 Result:",
              existingImageFiles.length,
              "files created"
            );
            existingImageFiles.forEach((f, i) => {
              console.log(
                `  File ${i + 1}:`,
                f?.name,
                f?.size,
                f?.type,
                "isFile:",
                f instanceof File
              );
            });
          } catch (err) {
            console.error("[JobForm] Step 2 ERROR:", err);
          }
        } else {
          console.log("[JobForm] Step 2: No existing images to convert");
        }

        // Step 3: Combine all reference images
        console.log("[JobForm] Step 3: Combining all images...");
        console.log("  - Product image files:", productImageFiles.length);
        console.log("  - Existing image files:", existingImageFiles.length);
        console.log("  - Uploaded reference images:", referenceImages.length);

        const allReferenceImages = [
          ...productImageFiles,
          ...existingImageFiles,
          ...referenceImages,
        ];

        console.log(
          "[JobForm] Step 3 Result: Total reference images:",
          allReferenceImages.length
        );

        // Verify each item in the array
        console.log("[JobForm] Verifying final array:");
        allReferenceImages.forEach((item, index) => {
          if (item instanceof File) {
            console.log(
              `  [${index}] ✅ File: ${item.name} (${item.size} bytes, ${item.type})`
            );
          } else if (item instanceof Blob) {
            console.log(
              `  [${index}] ⚠️ Blob: ${item.size} bytes, ${item.type}`
            );
          } else if (typeof item === "string") {
            console.log(
              `  [${index}] ❌ STRING (URL not converted): ${item.substring(
                0,
                50
              )}...`
            );
          } else {
            console.log(`  [${index}] ❌ UNKNOWN TYPE:`, typeof item, item);
          }
        });

        console.log("[JobForm] Step 4: Calling API...");
        console.log("  - Payload keys:", Object.keys(payload));
        console.log("  - Reference images to send:", allReferenceImages.length);
        console.log("  - Item images to send:", itemImages.length);

        if (mode === "edit" && initialJob) {
          console.log("[JobForm] Calling updateJob...");
          await updateJob(
            initialJob._id,
            payload,
            allReferenceImages,
            itemImages
          );
        } else {
          console.log("[JobForm] Calling addJob...");
          if (data.jobType === "REPAIR") {
            await addRepairJob(payload, allReferenceImages, itemImages);
          } else if (data.jobType === "ALTERATION") {
            await addAlterationJob(payload, allReferenceImages, itemImages);
          } else if (data.jobType === "INTERNAL") {
            await addInternalJob(payload, allReferenceImages);
          } else {
            await addJob(payload, allReferenceImages, itemImages);
          }
        }

        console.log("[JobForm] ========== SUBMIT SUCCESS ==========");
      } catch (err) {
        console.error("[JobForm] ========== SUBMIT ERROR ==========");
        console.error("[JobForm] Error:", err);
        toast.error(`Failed to ${mode === "edit" ? "update" : "create"} job`);
      } finally {
        setSubmitting(false);
      }
    },
    [
      mode,
      initialJob,
      selectedProduct,
      selectedUser,
      selectedOrder,
      selectedOrderItem,
      selectedKarigar,
      stones,
      activeProductImages,
      activeExistingImages,
      referenceImages,
      itemImages,
      needsCustomer,
      needsItemDetails,
      addJob,
      addRepairJob,
      addAlterationJob,
      addInternalJob,
      updateJob,
      convertUrlsToFiles,
    ]
  );

  // ============================================
  // RETURN
  // ============================================

  return {
    // Form
    form,
    errors,
    handleSubmit: handleSubmit(onSubmit),

    // Mode
    mode,
    isEditMode: mode === "edit",

    // Step navigation
    currentStep,
    setCurrentStep: goToStep,
    goToNextStep,
    goToPrevStep,
    canProceedToNext,

    // Loading states
    submitting,
    loading: jobLoading,

    // Watch values
    watchJobType,
    watchMetalType,
    watchProductType,
    watchIsUrgent,
    watchPriority,
    watchExpectedDate,

    // Derived state
    isStandaloneJob,
    isInternalJob,
    needsItemDetails,
    needsCustomer,
    canSelectProduct,

    // Karigar
    selectedKarigar,
    setSelectedKarigar,
    suggestedKarigars,
    showKarigarDialog,
    setShowKarigarDialog,

    // Stones
    stones,
    addStone,
    updateStone,
    removeStone,

    // Images
    referenceImages,
    setReferenceImages,
    itemImages,
    setItemImages,
    productImageUrls,
    removedProductImageIndexes,
    activeProductImages,
    handleRemoveProductImage,
    handleRestoreAllProductImages,
    handleRemoveAllProductImages,
    existingReferenceImages,
    existingItemImages,
    removedExistingImageIndexes,
    activeExistingImages,
    handleRemoveExistingImage,
    handleRestoreAllExistingImages,
    previewImage,
    setPreviewImage,

    // User
    selectedUser,
    handleUserSelected,
    clearSelectedUser,
    showUserDialog,
    setShowUserDialog,

    // Order
    selectedOrder,
    selectedOrderItem,
    handleOrderSelected,
    clearSelectedOrder,
    showOrderDialog,
    setShowOrderDialog,

    // Product
    selectedProduct,
    handleProductSelected,
    clearSelectedProduct,
    showProductDialog,
    setShowProductDialog,
  };
};

export default useJobForm;
